import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HandsPraying, Heart, ChatTeardropDots, Megaphone, Chalkboard, PaperPlaneTilt } from "@phosphor-icons/react";
import { API_BASE } from "../lib/site";
import SectionHeading from "../components/SectionHeading";
import Seo from "../components/Seo";

const REQUEST_TYPES = [
  { value: "prayer", label: "Prayer Request", icon: HandsPraying },
  { value: "emotional", label: "Emotional Support", icon: Heart },
  { value: "grief", label: "Grief Encouragement", icon: ChatTeardropDots },
  { value: "speaking", label: "Speaking Request", icon: Megaphone },
  { value: "workshop", label: "Workshop Information", icon: Chalkboard },
];

const RequestSupport = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    request_type: "prayer",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API_BASE}/support-requests`,
        {
          ...form,
          source_page:
            typeof window !== "undefined" ? window.location.href : null,
        },
        { withCredentials: false }
      );
      toast.success("Your request has been received. Check your inbox for a confirmation email.");
      setSubmitted(true);
      setForm({ name: "", email: "", phone: "", request_type: "prayer", subject: "", message: "" });
    } catch (err) {
      toast.error("We couldn't submit your request right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-request-support">
      <Seo
        title="Request Prayer & Chaplain Support"
        description="Reach out for prayer, grief support, or a chaplain conversation. Begin Again Living Ministries walks alongside you through loss and life's hardest seasons."
      />
      <section className="py-20 sm:py-28">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 reveal">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-primary font-semibold">
              <span className="w-8 h-px bg-secondary" />
              Request prayer or support
            </span>
            <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] text-ink-900">
              You don&rsquo;t have to find the right words.
            </h1>
            <p className="mt-6 text-ink-700 text-lg leading-relaxed">
              Share what you can — a prayer need, an ache, a heaviness, a
              question. Our chaplain-led team will read carefully, pray
              earnestly, and respond gently.
            </p>

            <div className="mt-10 p-7 bg-softblue/60 border border-ink-100 rounded-2xl">
              <h3 className="font-heading text-2xl text-ink-900 font-medium">
                Your privacy matters.
              </h3>
              <p className="mt-3 text-ink-700 leading-relaxed">
                Requests are confidential and seen only by ministry staff and
                trusted chaplain-led prayer partners. We never share your
                story without your permission.
              </p>
            </div>
          </div>

          <div className="lg:col-span-7 reveal">
            <form
              onSubmit={onSubmit}
              data-testid="support-request-form"
              className="bg-white rounded-2xl border border-ink-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-8 sm:p-10"
            >
              <h2 className="font-heading text-2xl text-ink-900 font-medium mb-2">
                How can we walk with you?
              </h2>
              <p className="text-ink-500 text-sm mb-7">
                All fields with * are required.
              </p>

              <div className="mb-6">
                <label className="block text-xs uppercase tracking-[0.18em] text-primary font-semibold mb-3">
                  What kind of support? *
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {REQUEST_TYPES.map(({ value, label, icon: Icon }) => (
                    <button
                      key={value}
                      type="button"
                      data-testid={`request-type-${value}`}
                      onClick={() => setField("request_type", value)}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 text-xs font-semibold transition-all ${
                        form.request_type === value
                          ? "border-primary bg-primary/5 text-primary"
                          : "border-ink-200 text-ink-700 hover:border-primary/40"
                      }`}
                    >
                      <Icon size={22} weight="duotone" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-semibold text-ink-800 mb-2">Name *</label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setField("name", e.target.value)}
                    data-testid="support-input-name"
                    className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-ink-800 mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setField("email", e.target.value)}
                    data-testid="support-input-email"
                    className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-ink-800 mb-2">Phone (optional)</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setField("phone", e.target.value)}
                    data-testid="support-input-phone"
                    className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-ink-800 mb-2">Subject (optional)</label>
                  <input
                    type="text"
                    value={form.subject}
                    onChange={(e) => setField("subject", e.target.value)}
                    data-testid="support-input-subject"
                    className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="block text-sm font-semibold text-ink-800 mb-2">Your message *</label>
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={(e) => setField("message", e.target.value)}
                  placeholder="Share as much or as little as you'd like. We read every word."
                  data-testid="support-input-message"
                  className="w-full rounded-xl border border-ink-200 px-4 py-3 bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-y"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="support-submit-btn"
                className="mt-8 inline-flex items-center gap-2 bg-primary text-white rounded-full px-7 py-3.5 font-semibold hover:bg-primary-dark hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 disabled:opacity-60"
              >
                <PaperPlaneTilt size={18} weight="duotone" />
                {submitting ? "Sending..." : "Send My Request"}
              </button>

              {submitted && (
                <p data-testid="support-success-msg" className="mt-5 text-sm text-primary font-semibold">
                  Thank you — your request has been received with care.
                </p>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RequestSupport;
